import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { peekPersistentItem } from '../vrs-auth/storage';
import { SLOW_STAGE_MS, deriveCallStage, isSlowableStage } from './peerWaiting';
/**
 * The call this device is in (vrs_active_call), or ''.
 *
 * @returns {string}
 */
function storedCallId() {
    try {
        const callId = JSON.parse(peekPersistentItem('vrs_active_call') || 'null')?.callId;
        return typeof callId === 'string' ? callId : '';
    }
    catch {
        return '';
    }
}
/**
 * The peer pane's current stage, and whether it has run past SLOW_STAGE_MS.
 *
 * The stage comes from Redux (the `features/vrs-layout` call-room slice plus
 * the peer facts the layout passes in). "Slow" is the one fact Redux does not
 * hold: a timer started when the stage began, so a stall reads as a stall.
 * Every stage change takes a new token, so a stage that comes back later (the
 * peer leaves and we wait again) starts its clock over and a timer from an
 * earlier stage can never mark the current one slow.
 */
export function useCallStage(peer) {
    const room = useSelector((state) => state['features/vrs-layout']);
    // The peer put this call on hold (call_on_hold for the call this device
    // is in). The stored call is peeked, never read with a write-back, and
    // only while a hold is on.
    const onHold = useSelector((state) => {
        const hold = state['features/call-management'];
        return Boolean(hold?.onHold && hold.holdCallId && storedCallId() === hold.holdCallId);
    });
    const stage = deriveCallStage(room, {
        ...peer,
        onHold
    });
    const clock = useRef({ stage, token: 0 });
    if (clock.current.stage !== stage) {
        clock.current = { stage, token: clock.current.token + 1 };
    }
    const { token } = clock.current;
    const [slowToken, setSlowToken] = useState(-1);
    useEffect(() => {
        if (!isSlowableStage(stage)) {
            return undefined;
        }
        const timer = setTimeout(() => setSlowToken(token), SLOW_STAGE_MS);
        return () => clearTimeout(timer);
    }, [stage, token]);
    return {
        slow: slowToken === token && isSlowableStage(stage),
        stage
    };
}
